import { Sparkles, Palette, Truck, ShieldCheck, Clock, Heart } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import PageBanner from "../components/PageBanner";
import { whyUs } from "../data/siteData";
import { optimizeImg } from "../lib/img";
import { useZeoContent } from "../lib/ZeoContentContext";
import { cn } from "../lib/utils";

const icons = { Sparkles, Palette, Truck, ShieldCheck, Clock, Heart };

const stats = [
  { value: "15+", label: "Yıllık Deneyim" },
  { value: "10.000+", label: "Tamamlanan Sipariş" },
  { value: "81", label: "İle Kargo İmkanı" },
  { value: "5.0", label: "Google Puanı" },
];

const ABOUT_IMG = "https://images.unsplash.com/photo-1487070183336-b863922373d4?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function About() {
  const { aboutText, business } = useZeoContent();
  // Panelden girilen metin varsa satır aralarına göre paragraflara bölünür;
  // yoksa sitenin varsayılan hikaye metni gösterilir.
  const aboutParagraphs = aboutText
    ? aboutText.split(/\n+/).map(p => p.trim()).filter(Boolean)
    : null;

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="about-page">
      <Header />

      <PageBanner
        eyebrow="Kurumsal"
        title="Hikayemiz"
        subtitle={`${business.name}, Dörtyol'da küçük bir atölyede başladığı yolculuğuna bugün Hatay, Adana, Osmaniye ve Gaziantep'e uzanan kurye ağı ve Türkiye geneline kargo hizmetiyle devam ediyor.`}
        breadcrumb={[{ label: "Hakkımızda" }]}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="frida-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] rounded-[32px] overflow-hidden bg-[#EAEAEA]">
              <img
                src={optimizeImg(ABOUT_IMG, 800)}
                alt="Frida Çiçekçilik atölyesi"
                width="800"
                height="1000"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="text-xs tracking-[0.25em] uppercase text-[#A85C34] font-medium mb-3">
              · Bizi Biz Yapan ·
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#222222] tracking-tight leading-[1.15] mb-6">
              Her Aranjmanda <span className="italic text-[#A85C34]">Bir Hikaye</span> Var
            </h2>
            {aboutParagraphs ? (
              aboutParagraphs.map((p, i) => (
                <p key={i} className={cn("text-[#555555] leading-relaxed", i === aboutParagraphs.length - 1 ? "mb-8" : "mb-5")}>
                  {p}
                </p>
              ))
            ) : (
              <>
                <p className="text-[#555555] leading-relaxed mb-5">
                  Frida Çiçekçilik, Dörtyol/Hatay merkezli olarak kurulduğu günden bu yana
                  "her çiçek bir duyguyu anlatır" ilkesiyle çalışıyor. Deneyimli florist ekibimiz, ithal ve
                  yerli çiçekleri özenle seçerek doğum gününden düğüne, açılıştan taziyeye kadar her an için
                  zarafeti elden bırakmayan aranjmanlar hazırlıyor.
                </p>
                <p className="text-[#555555] leading-relaxed mb-8">
                  Bugün Hatay, Adana, Osmaniye ve Gaziantep'te aynı gün teslimat, Türkiye'nin her ilinde ise anlaşmalı
                  kargo hizmeti sunarak, sevdiklerinize olan mesafeyi bir çiçek kadar kısaltmayı sürdürüyoruz.
                </p>
              </>
            )}

            <div className="grid grid-cols-2 gap-6">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-3xl md:text-4xl text-[#A85C34] font-semibold">{s.value}</div>
                  <div className="text-sm text-[#888888] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F8F8F8]">
        <div className="frida-container">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs tracking-[0.25em] uppercase text-[#A85C34] font-medium mb-3">
              · Değerlerimiz ·
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#222222] tracking-tight leading-[1.1]">
              Neden <span className="italic text-[#A85C34]">Frida Çiçekçilik</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((w) => {
              const Icon = icons[w.icon];
              return (
                <div key={w.title} className="bg-white rounded-3xl border border-[#EAEAEA] p-8 lift-card">
                  <div className="w-14 h-14 rounded-2xl bg-[#F8F8F8] flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#A85C34]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#222222] mb-2">{w.title}</h3>
                  <p className="text-[#555555] leading-relaxed text-sm">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
